import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import { Card } from '../components/ui';

const PERIODS = [
  { value: '3d', label: 'Ultimi 3 giorni' },
  { value: '1w', label: 'Ultima settimana' },
  { value: '2w', label: 'Ultime 2 settimane' },
  { value: '1m', label: 'Ultimo mese' },
  { value: '6m', label: 'Ultimi 6 mesi' },
  { value: '1y', label: 'Ultimo anno' },
  { value: '5y', label: 'Ultimi 5 anni' },
];

const CHART_WINDOWS = [
  { value: '1h', label: '1 ora' },
  { value: '12h', label: '12 ore' },
  { value: '1d', label: '1 giorno' },
  { value: '7d', label: '7 giorni' },
  { value: '30d', label: '30 giorni' },
];

const SENSOR_STYLES = {
  'TEMP-01': {
    stroke: '#2563eb',
    thresholdStroke: '#2563eb',
    label: 'Sonda 1',
  },
  'TEMP-02': {
    stroke: '#16a34a',
    thresholdStroke: '#16a34a',
    label: 'Sonda 2',
  },
  'TEMP-03': {
    stroke: '#ea580c',
    thresholdStroke: '#ea580c',
    label: 'Sonda 3',
  },
  'TEMP-04': {
    stroke: '#9333ea',
    thresholdStroke: '#9333ea',
    label: 'Sonda 4',
  },
};

function normalizeSensorId(sensorId = '') {
  const raw = String(sensorId || '').trim();

  // TEMP-1 -> TEMP-01, e lo stesso per le umidita' HUM-* del Pulse.
  const match = raw.match(/^(TEMP|HUM)-0?(\d+)$/i);

  if (match) {
    const n = Number(match[2]);

    if (Number.isFinite(n) && n > 0) {
      return `${match[1].toUpperCase()}-${String(n).padStart(2, '0')}`;
    }
  }

  return raw;
}

// Etichetta di base: CH1..CH6 per i PT100, MBn per le temperature delle sonde
// del bus (TEMP-07 e seguenti), UMn per le umidita'.
function defaultSensorLabel(sensorId = '') {
  const n = Number(String(sensorId).replace(/\D/g, '')) || 0;

  if (sensorId.startsWith('HUM-')) return `UM${n}`;
  if (n > 6) return `MB${n - 6}`;

  return SENSOR_STYLES[sensorId]?.label || `CH${n}`;
}

function sensorSortKey(sensorId = '') {
  const n = Number(String(sensorId).replace(/\D/g, '')) || 0;

  return (String(sensorId).startsWith('HUM-') ? 1000 : 0) + n;
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  window.URL.revokeObjectURL(url);
}

function getCustomerLabelFromDevice(device) {
  const customer = device?.machineRef?.customerUserId;

  if (!customer) return device?.customer_name || '-';

  const fullName = [customer.nome, customer.cognome]
    .filter(Boolean)
    .join(' ')
    .trim();

  return fullName || customer.username || customer.matricola || '-';
}

function getDeviceMachinesLabel(device) {
  const names = new Set();

  if (Array.isArray(device?.channels)) {
    device.channels.forEach((channel) => {
      const machine = channel?.machineRef;

      if (machine?.nome) {
        names.add(machine.nome);
      }
    });
  }

  if (device?.machineRef?.nome) {
    names.add(device.machineRef.nome);
  }

  return names.size ? Array.from(names).join(', ') : '-';
}

function getDeviceDepartmentsLabel(device) {
  const names = new Set();

  if (Array.isArray(device?.channels)) {
    device.channels.forEach((channel) => {
      const department =
        channel?.departmentRef || channel?.machineRef?.departmentRef;

      if (department?.name) {
        names.add(department.name);
      }
    });
  }

  const fallbackDepartment = device?.machineRef?.departmentRef;

  if (fallbackDepartment?.name) {
    names.add(fallbackDepartment.name);
  }

  return names.size ? Array.from(names).join(', ') : '-';
}

function TemperatureHistoryChart({ chartData, loading }) {
  if (loading) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center">
        <p className="text-sm text-gray-500">
          Caricamento grafico in corso...
        </p>
      </div>
    );
  }

  const points = Array.isArray(chartData?.points) ? chartData.points : [];
  const selectedSensorId = normalizeSensorId(chartData?.selectedSensorId || '');

  const selectedStyle = SENSOR_STYLES[selectedSensorId] || {
    stroke: '#2563eb',
    thresholdStroke: '#2563eb',
    label: selectedSensorId ? defaultSensorLabel(selectedSensorId) : 'Sonda',
  };

  const alarms = Array.isArray(chartData?.alarms) ? chartData.alarms : [];

  // Le umidita' delle sonde del bus (HUM-*) sono in %RH, il resto in gradi.
  const isHumidity = selectedSensorId.startsWith('HUM-');
  const unit = isHumidity ? '%RH' : '°C';
  const axisLabel = isHumidity ? 'Umidità (%RH)' : 'Temperatura (°C)';

  const yDomain = Array.isArray(chartData?.yDomain)
    ? chartData.yDomain
    : ['auto', 'auto'];

  if (!points.length || !selectedSensorId) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center">
        <p className="text-sm text-gray-500">
          Nessun dato disponibile per il grafico.
        </p>
      </div>
    );
  }

  const formatValue = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : '-';
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const visiblePayload = payload.filter(
      (item) => item.value !== null && item.value !== undefined
    );

    return (
      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-lg">
        <p className="text-xs text-gray-500 mb-2">
          {payload[0]?.payload?.label || label}
        </p>

        <div className="space-y-1">
          {visiblePayload.map((item) => (
            <p key={item.dataKey} className="text-sm text-gray-800">
              <span className="font-semibold">{selectedStyle.label}</span>
              {': '}
              {formatValue(item.value)} {unit}
            </p>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="h-[430px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={points}
          margin={{ top: 20, right: 26, left: 0, bottom: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={{ stroke: '#d1d5db' }}
            tickLine={{ stroke: '#d1d5db' }}
            minTickGap={28}
          />

          <YAxis
            domain={yDomain}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={{ stroke: '#d1d5db' }}
            tickLine={{ stroke: '#d1d5db' }}
            tickFormatter={(value) => `${value}`}
            label={{
              value: axisLabel,
              angle: -90,
              position: 'insideLeft',
              style: {
                fill: '#6b7280',
                fontSize: 12,
                textAnchor: 'middle',
              },
            }}
          />

          <Tooltip content={<CustomTooltip />} />
          <Legend />

          {alarms.map((alarm, index) => (
            <React.Fragment key={`alarm-${selectedSensorId}-${index}`}>
              {Number.isFinite(Number(alarm.max)) && (
                <ReferenceLine
                  y={Number(alarm.max)}
                  stroke={selectedStyle.thresholdStroke}
                  strokeDasharray="6 4"
                  label={{
                    value: `Max ${Number(alarm.max)} ${unit}`,
                    position: 'right',
                    fill: selectedStyle.thresholdStroke,
                    fontSize: 11,
                  }}
                />
              )}

              {Number.isFinite(Number(alarm.min)) && (
                <ReferenceLine
                  y={Number(alarm.min)}
                  stroke={selectedStyle.thresholdStroke}
                  strokeDasharray="2 4"
                  label={{
                    value: `Min ${Number(alarm.min)} ${unit}`,
                    position: 'right',
                    fill: selectedStyle.thresholdStroke,
                    fontSize: 11,
                  }}
                />
              )}
            </React.Fragment>
          ))}

          <Line
            type="monotone"
            dataKey="value"
            name={selectedStyle.label || selectedSensorId}
            stroke={selectedStyle.stroke}
            strokeWidth={2.8}
            dot={false}
            connectNulls
            activeDot={{ r: 5, strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function StoricoLetture({
  embedded = false,
  onBack = null,
  devicesOverride = null,
}) {
  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [period, setPeriod] = useState('3d');

  const [loadingDevices, setLoadingDevices] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const [chartWindow, setChartWindow] = useState('1h');
  const [selectedSensorId, setSelectedSensorId] = useState('');
  const [chartData, setChartData] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);

  const externalDevices = useMemo(() => {
    return Array.isArray(devicesOverride) ? devicesOverride : null;
  }, [devicesOverride]);

  const selectedDevice = useMemo(() => {
    return (
      devices.find((device) => device.device_id === selectedDeviceId) || null
    );
  }, [devices, selectedDeviceId]);

  const availableSensors = useMemo(() => {
    const sensors = [];

    const isProbe = (sensorId) => /^(TEMP|HUM)-\d+$/.test(sensorId);

    if (Array.isArray(selectedDevice?.channels)) {
      selectedDevice.channels.forEach((channel) => {
        if (channel?.enabled === false) return;

        const sensorId = normalizeSensorId(channel?.sensor_id || '');

        if (sensorId && isProbe(sensorId)) {
          sensors.push({
            sensor_id: sensorId,
            label: channel.label || defaultSensorLabel(sensorId),
          });
        }
      });
    }

    /*
      Anche le sonde che il device trasmette ma che non sono ancora censite fra
      i canali (Pulse appena collegato, o canali salvati quando la pagina
      ne gestiva solo quattro): cosi' lo storico si consulta lo stesso.
    */
    if (Array.isArray(selectedDevice?.last_measures)) {
      selectedDevice.last_measures.forEach((measure) => {
        const sensorId = normalizeSensorId(measure?.sensor_id || '');

        if (sensorId && isProbe(sensorId)) {
          sensors.push({
            sensor_id: sensorId,
            label: defaultSensorLabel(sensorId),
          });
        }
      });
    }

    const unique = new Map();

    sensors.forEach((sensor) => {
      if (!unique.has(sensor.sensor_id)) {
        unique.set(sensor.sensor_id, sensor);
      }
    });

    return Array.from(unique.values()).sort(
      (a, b) => sensorSortKey(a.sensor_id) - sensorSortKey(b.sensor_id)
    );
  }, [selectedDevice]);

  const periodLabel =
    PERIODS.find((item) => item.value === period)?.label || period;

  const loadDevices = async () => {
    if (externalDevices) {
      const list = externalDevices.filter((device) => device?.device_id);
      setDevices(list);
      setError('');
      return;
    }

    try {
      setLoadingDevices(true);
      setError('');

      const res = await authFetch('/api/iot/devices');

      if (!res || !res.ok) {
        throw new Error('Errore nel recupero dispositivi');
      }

      const data = await res.json();
      const list = Array.isArray(data) ? data : [];

      setDevices(list);
    } catch (err) {
      console.error('loadDevices error:', err);
      setError(err?.message || 'Impossibile caricare i dispositivi.');
      setDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  };

  const loadChart = async () => {
    if (!selectedDeviceId || !selectedSensorId) {
      setChartData(null);
      return;
    }

    try {
      setLoadingChart(true);
      setError('');

    const res = await authFetch(
      `/api/iot/devices/${encodeURIComponent(
        selectedDeviceId
      )}/telemetry/chart?window=${encodeURIComponent(
        chartWindow
      )}&sensorId=${encodeURIComponent(selectedSensorId)}`
    );

    if (!res) {
      throw new Error('Sessione scaduta o connessione non disponibile');
    }

    const data = await res.json().catch(() => null);

    if (!res.ok) {
      throw new Error(data?.message || 'Errore nel recupero grafico');
    }


      setChartData(data || null);
    } catch (err) {
      console.error('loadChart error:', err);
      setError(err?.message || 'Impossibile caricare il grafico.');
      setChartData(null);
    } finally {
      setLoadingChart(false);
    }
  };

  const handleExportCsv = async () => {
    if (!selectedDeviceId) return;

    try {
      setExporting(true);
      setError('');

      const res = await authFetch(
        `/api/iot/devices/${encodeURIComponent(
          selectedDeviceId
        )}/telemetry/export.csv?period=${encodeURIComponent(period)}`
      );

      if (!res || !res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || 'Errore durante export CSV');
      }

      const blob = await res.blob();
      const filename = `storico_${selectedDeviceId}_${period}.csv`;

      downloadBlob(blob, filename);
    } catch (err) {
      console.error('handleExportCsv error:', err);
      setError(err?.message || 'Impossibile scaricare il CSV.');
    } finally {
      setExporting(false);
    }
  };

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }

    navigate('/dashboard-cliente');
  };

  useEffect(() => {
    loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalDevices]);

  useEffect(() => {
    if (!devices.length) {
      setSelectedDeviceId('');
      return;
    }

    const selectedStillValid = devices.some(
      (device) => device.device_id === selectedDeviceId
    );

    if (!selectedDeviceId || !selectedStillValid) {
      setSelectedDeviceId(devices[0].device_id);
    }
  }, [devices, selectedDeviceId]);

  useEffect(() => {
    if (!availableSensors.length) {
      setSelectedSensorId('');
      return;
    }

    if (
      !selectedSensorId ||
      !availableSensors.some(
        (sensor) => sensor.sensor_id === selectedSensorId
      )
    ) {
      setSelectedSensorId(availableSensors[0].sensor_id);
    }
  }, [availableSensors, selectedSensorId]);

  useEffect(() => {
    loadChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceId, selectedSensorId, chartWindow]);

return (
  <div className={embedded ? '' : 'min-h-screen bg-gray-100 pb-10'}>
    <div
      className={
        embedded
          ? 'space-y-6'
          : 'max-w-7xl mx-auto px-4 pt-10 space-y-6'
      }
    >
        <Card className="shadow-md p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Storico letture
              </h1>

              <p className="text-sm text-gray-500 mt-2">
                Seleziona l’ESP32, scegli il periodo da esportare e scarica il CSV.
              </p>
            </div>

            <button
              type="button"
              onClick={handleBack}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm font-medium transition"
            >
              {embedded ? 'Torna al riepilogo' : 'Torna alla dashboard'}
            </button>
          </div>
        </Card>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm shadow-sm">
            {error}
          </div>
        )}

        <Card className="shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Esporta dati CSV
          </h2>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">
                ESP32
              </label>

              <select
                value={selectedDeviceId}
                onChange={(e) => setSelectedDeviceId(e.target.value)}
                disabled={loadingDevices}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
              >
                {devices.length === 0 && (
                  <option value="">
                    Nessun dispositivo
                  </option>
                )}

                {devices.map((device) => (
                  <option key={device.device_id} value={device.device_id}>
                    {device.device_id}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">
                Periodo CSV
              </label>

              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
              >
                {PERIODS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                type="button"
                onClick={handleExportCsv}
                disabled={!selectedDeviceId || exporting}
                className="w-full px-5 py-3 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition disabled:opacity-60"
              >
                {exporting ? 'Preparazione CSV...' : 'Scarica CSV'}
              </button>
            </div>
          </div>
        </Card>

        {selectedDevice && (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Card className="shadow-sm p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Cliente
              </p>
              <p className="mt-2 text-lg font-bold text-gray-800">
                {getCustomerLabelFromDevice(selectedDevice)}
              </p>
            </Card>

            <Card className="shadow-sm p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Reparto
              </p>
              <p className="mt-2 text-lg font-bold text-gray-800">
                {getDeviceDepartmentsLabel(selectedDevice)}
              </p>
            </Card>

            <Card className="shadow-sm p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Macchina
              </p>
              <p className="mt-2 text-lg font-bold text-gray-800">
                {getDeviceMachinesLabel(selectedDevice)}
              </p>
            </Card>

            <Card className="shadow-sm p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Periodo CSV
              </p>
              <p className="mt-2 text-lg font-bold text-gray-800">
                {periodLabel}
              </p>
            </Card>
          </div>
        )}

        <Card className="shadow-md p-6">
          <div className="flex flex-col gap-4 mb-5">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  Andamento temperature
                </h2>

                <p className="text-sm text-gray-500 mt-1">
                  Scegli una sonda e una finestra temporale. Il grafico mostra una sola sonda con le sue soglie.
                </p>
              </div>

              <button
                type="button"
                onClick={loadChart}
                disabled={loadingChart || !selectedDeviceId || !selectedSensorId}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition disabled:opacity-60"
              >
                {loadingChart ? 'Caricamento...' : 'Aggiorna grafico'}
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Sonda
                </label>

                <select
                  value={selectedSensorId}
                  onChange={(e) => setSelectedSensorId(e.target.value)}
                  disabled={!availableSensors.length}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                >
                  {availableSensors.length === 0 && (
                    <option value="">
                      Nessuna sonda disponibile
                    </option>
                  )}

                  {availableSensors.map((sensor) => (
                    <option key={sensor.sensor_id} value={sensor.sensor_id}>
                      {sensor.label} - {sensor.sensor_id}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Finestra temporale
                </label>

                <select
                  value={chartWindow}
                  onChange={(e) => setChartWindow(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                >
                  {CHART_WINDOWS.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <TemperatureHistoryChart
            chartData={chartData}
            loading={loadingChart}
          />
        </Card>
      </div>
    </div>
  );
}